import { useEffect, useMemo, useState } from "react";
import {
  Database,
  Activity,
  AlertTriangle,
  Gauge,
  HardDrive,
  Layers,
  Zap,
  RefreshCw,
  Search,
  Play,
  Pause,
  Maximize2,
  X,
  ShieldCheck,
} from "lucide-react";
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { HeaderKpi, SectionCard, CenterHeader } from "./center/CenterPrimitives";

type Range = "15m" | "1h" | "24h";
type Level = "ERROR" | "WARN" | "INFO" | "FATAL";
type Tab = "db" | "query" | "logs" | "relations" | "security";

const TABS: { id: Tab; label: string }[] = [
  { id: "db", label: "Database Metrics" },
  { id: "query", label: "Query Execution" },
  { id: "logs", label: "Error Logs" },
  { id: "relations", label: "Relation Metrics" },
  { id: "security", label: "Security" },
];

const RANGE_META: Record<Range, { points: number; label: string; unit: string }> = {
  "15m": { points: 30, label: "15 min", unit: "30s" },
  "1h": { points: 60, label: "1 h", unit: "1 min" },
  "24h": { points: 96, label: "24 h", unit: "15 min" },
};

const series = (seed: number, n: number, base: number, amp: number, tick: number) =>
  Array.from({ length: n }, (_, i) => ({
    x: i,
    y: Math.max(
      0,
      base +
        Math.sin(i / 2 + seed + tick / 7) * amp +
        Math.cos(i / 3 + seed - tick / 11) * (amp / 2),
    ),
  }));

const tooltipStyle = {
  backgroundColor: "oklch(0.18 0.02 240)",
  border: "1px solid oklch(1 0 0 / 0.1)",
  borderRadius: 8,
  fontSize: 11,
  color: "white",
} as const;

const G = "oklch(0.78 0.18 150)";
const Y = "oklch(0.82 0.16 85)";
const R = "oklch(0.7 0.2 35)";
const B = "oklch(0.7 0.15 240)";
const P = "oklch(0.7 0.18 290)";

function withAlpha(color: string, alpha: number) {
  return color.replace(")", ` / ${alpha})`);
}

function MiniChart({
  data,
  color,
  type = "area",
}: {
  data: { x: number; y: number }[];
  color: string;
  type?: "area" | "line" | "bar";
}) {
  const gid = `g${Math.round(Math.random() * 1e6)}`;
  return (
    <div className="h-32 w-full">
      <ResponsiveContainer width="100%" height="100%">
        {type === "area" ? (
          <AreaChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={withAlpha(color, 0.4)} />
                <stop offset="100%" stopColor={withAlpha(color, 0)} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="oklch(1 0 0 / 0.04)" vertical={false} />
            <XAxis dataKey="x" hide />
            <YAxis hide />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: withAlpha(color, 0.3) }} />
            <Area type="monotone" dataKey="y" stroke={color} strokeWidth={1.5} fill={`url(#${gid})`} />
          </AreaChart>
        ) : type === "line" ? (
          <LineChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="oklch(1 0 0 / 0.04)" vertical={false} />
            <XAxis dataKey="x" hide />
            <YAxis hide />
            <Tooltip contentStyle={tooltipStyle} cursor={{ stroke: withAlpha(color, 0.3) }} />
            <Line type="monotone" dataKey="y" stroke={color} strokeWidth={1.75} dot={false} />
          </LineChart>
        ) : (
          <BarChart data={data} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="oklch(1 0 0 / 0.04)" vertical={false} />
            <XAxis dataKey="x" hide />
            <YAxis hide />
            <Tooltip contentStyle={tooltipStyle} cursor={{ fill: withAlpha(color, 0.08) }} />
            <Bar dataKey="y" fill={color} radius={[3, 3, 0, 0]} />
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

function MetricBlock({
  label,
  value,
  unit,
  data,
  color,
  type = "area",
}: {
  label: string;
  value: string;
  unit?: string;
  data: { x: number; y: number }[];
  color: string;
  type?: "area" | "line" | "bar";
}) {
  return (
    <div className="rounded-xl bg-white/[0.02] p-3 ring-1 ring-white/5">
      <div className="flex items-baseline justify-between">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-xs">
          <span className="font-medium">{value}</span>
          {unit && <span className="ml-1 text-muted-foreground">{unit}</span>}
        </div>
      </div>
      <MiniChart data={data} color={color} type={type} />
    </div>
  );
}

type LogEntry = { ts: string; level: Level; source: string; msg: string };

const ALL_LOGS: LogEntry[] = [
  { ts: "14:32:08", level: "ERROR", source: "auth.users", msg: "duplicate key value violates unique constraint" },
  { ts: "14:28:51", level: "INFO", source: "checkpointer", msg: "checkpoint complete: wrote 2438 buffers (1.5%)" },
  { ts: "14:21:44", level: "WARN", source: "walreceiver", msg: "replication slot lag exceeds 30s" },
  { ts: "14:12:03", level: "INFO", source: "autovacuum", msg: "automatic vacuum of table public.consultations" },
  { ts: "13:58:11", level: "FATAL", source: "autovacuum", msg: "canceling statement due to lock timeout" },
  { ts: "13:42:03", level: "ERROR", source: "public.consultations", msg: "deadlock detected · pid 24817" },
  { ts: "13:21:18", level: "WARN", source: "bgwriter", msg: "buffers_backend high · 18% of writes" },
  { ts: "12:19:57", level: "WARN", source: "checkpointer", msg: "checkpoints occurring too frequently" },
  { ts: "11:58:02", level: "INFO", source: "postgres", msg: "received SIGHUP, reloading configuration files" },
  { ts: "11:14:36", level: "ERROR", source: "storage.objects", msg: "could not write to file: No space left on device" },
];

function LogRow({ entry }: { entry: LogEntry }) {
  const tone =
    entry.level === "FATAL" || entry.level === "ERROR"
      ? R
      : entry.level === "WARN"
        ? Y
        : B;
  return (
    <li className="grid grid-cols-[80px_60px_140px_1fr] items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2 text-[11px]">
      <span className="font-mono text-muted-foreground">{entry.ts}</span>
      <span
        className="rounded px-1.5 py-0.5 text-center text-[10px] font-medium"
        style={{ background: withAlpha(tone, 0.18), color: tone }}
      >
        {entry.level}
      </span>
      <span className="truncate font-mono text-muted-foreground">{entry.source}</span>
      <span className="truncate">{entry.msg}</span>
    </li>
  );
}

function RelationRow({
  name,
  size,
  rows,
  ratio,
  tone,
}: {
  name: string;
  size: string;
  rows: string;
  ratio: number;
  tone: "success" | "warning" | "danger";
}) {
  const color = tone === "danger" ? R : tone === "warning" ? Y : G;
  return (
    <li className="grid grid-cols-[1fr_72px_72px_120px] items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2 text-[11px]">
      <span className="truncate font-mono">{name}</span>
      <span className="text-right text-muted-foreground">{size}</span>
      <span className="text-right text-muted-foreground">{rows}</span>
      <span className="flex items-center gap-2">
        <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
          <span className="block h-full" style={{ width: `${ratio}%`, background: color }} />
        </span>
        <span className="w-8 text-right text-muted-foreground">{ratio}%</span>
      </span>
    </li>
  );
}

function Toolbar({
  range,
  setRange,
  auto,
  setAuto,
  onRefresh,
  lastSync,
}: {
  range: Range;
  setRange: (r: Range) => void;
  auto: boolean;
  setAuto: (v: boolean) => void;
  onRefresh: () => void;
  lastSync: Date;
}) {
  return (
    <div className="card-surface flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2">
      <div className="inline-flex items-center gap-1 rounded-full bg-white/5 p-1">
        {(["15m", "1h", "24h"] as Range[]).map((r) => (
          <button
            key={r}
            onClick={() => setRange(r)}
            className={
              "rounded-full px-3 py-1 text-[11px] font-medium transition " +
              (range === r ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")
            }
          >
            {r}
          </button>
        ))}
      </div>
      <div className="ml-auto flex items-center gap-2 text-[11px] text-muted-foreground">
        <span className="hidden sm:inline">
          Sync : {lastSync.toLocaleTimeString()} · {RANGE_META[range].label} · pas {RANGE_META[range].unit}
        </span>
        <button
          onClick={() => setAuto(!auto)}
          className={
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 " +
            (auto
              ? "bg-success/15 text-success ring-success/30"
              : "bg-white/5 text-muted-foreground ring-white/10 hover:text-foreground")
          }
          title="Auto-refresh (5s)"
        >
          {auto ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
          {auto ? "Live" : "Pause"}
        </button>
        <button
          onClick={onRefresh}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-[11px] text-muted-foreground ring-1 ring-white/10 hover:text-foreground"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>
    </div>
  );
}

export default function PostgresMetricsView({ onBack }: { onBack: () => void }) {
  const [tab, setTab] = useState<Tab>("db");
  const [fullscreen, setFullscreen] = useState(false);
  const [range, setRange] = useState<Range>("1h");
  const [auto, setAuto] = useState(true);
  const [tick, setTick] = useState(0);
  const [lastSync, setLastSync] = useState(new Date());
  const [query, setQuery] = useState("");
  const [levels, setLevels] = useState<Set<Level>>(new Set(["ERROR", "WARN", "INFO", "FATAL"]));

  useEffect(() => {
    if (!auto) return;
    const id = setInterval(() => {
      setTick((t) => t + 1);
      setLastSync(new Date());
    }, 5000);
    return () => clearInterval(id);
  }, [auto]);

  const refresh = () => {
    setTick((t) => t + 1);
    setLastSync(new Date());
  };

  const n = RANGE_META[range].points;

  const charts = useMemo(
    () => ({
      conn: series(1, n, 35, 12, tick),
      tx: series(2, n, 800, 300, tick),
      tuples: series(3, n, 30000, 12000, tick),
      cache: series(4, n, 95, 4, tick),
      blkR: series(5, n, 2000, 800, tick),
      blkW: series(6, n, 900, 300, tick),
      dead: series(7, n, 0, 1, tick),
      lag: series(8, n, 30, 12, tick),
      qAvg: series(9, n, 14, 5, tick),
      qP95: series(10, n, 90, 30, tick),
      qps: series(11, n, 2200, 600, tick),
      slow: series(12, n, 5, 4, tick),
      err: series(13, Math.min(n, 48), 4, 3, tick),
      warn: series(14, Math.min(n, 48), 8, 4, tick),
      fatal: series(15, Math.min(n, 48), 0, 1, tick),
      seq: series(16, n, 40, 15, tick),
      idx: series(17, n, 2000, 500, tick),
      hit: series(18, n, 99, 1, tick),
      vac: series(19, Math.min(n, 48), 4, 2, tick),
    }),
    [n, tick],
  );

  const toggleLevel = (lvl: Level) => {
    const next = new Set(levels);
    if (next.has(lvl)) next.delete(lvl);
    else next.add(lvl);
    setLevels(next);
  };

  const filteredLogs = ALL_LOGS.filter(
    (l) =>
      levels.has(l.level) &&
      (query === "" ||
        l.msg.toLowerCase().includes(query.toLowerCase()) ||
        l.source.toLowerCase().includes(query.toLowerCase())),
  );

  const LEVEL_TONE: Record<Level, string> = {
    ERROR: R,
    FATAL: R,
    WARN: Y,
    INFO: B,
  };

  return (
    <div className="flex flex-col gap-5">
      <CenterHeader
        title="PostgreSQL — Database Metrics"
        scope="Supabase primary · prod-eu-west-1 · 15.4 · uptime 47d"
        onBack={onBack}
      />

      <Toolbar range={range} setRange={setRange} auto={auto} setAuto={setAuto} onRefresh={refresh} lastSync={lastSync} />

      <div className="card-surface flex flex-wrap items-center gap-1 rounded-full p-1 w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={
              "rounded-full px-3.5 py-1.5 text-[11px] font-medium transition " +
              (tab === t.id ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground")
            }
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <HeaderKpi icon={Database} value="42" label="Active connections" delta="+3" tone="success" seed={1} />
        <HeaderKpi icon={Gauge} value="68%" label="Cache hit ratio" delta="+2%" tone="success" seed={2} />
        <HeaderKpi icon={HardDrive} value="187 GB" label="Database size" delta="+1.2GB" tone="warning" seed={3} />
        <HeaderKpi icon={AlertTriangle} value="14" label="Errors (24h)" delta="+4" tone="danger" seed={4} />
      </div>

      {tab === "db" && (
        <div className="relative">
          <button
            onClick={() => setFullscreen(true)}
            className="absolute right-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-foreground ring-1 ring-white/15 hover:bg-white/15"
          >
            <Maximize2 className="h-3 w-3" /> Ouvrir
          </button>
          <SectionCard title="Database Metrics" scope={`Live · Fenêtre ${RANGE_META[range].label} · pas ${RANGE_META[range].unit}`} icon={Database} tone="success">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricBlock label="Connections" value="42" unit="/ 100" data={charts.conn} color={G} />
              <MetricBlock label="Transactions/s" value="1.2k" data={charts.tx} color={B} type="line" />
              <MetricBlock label="Tuples fetched" value="48k" unit="/s" data={charts.tuples} color={P} type="area" />
              <MetricBlock label="Buffer cache hit" value="98.4" unit="%" data={charts.cache} color={G} type="line" />
              <MetricBlock label="Blocks read" value="3.2k" unit="/s" data={charts.blkR} color={Y} type="bar" />
              <MetricBlock label="Blocks written" value="1.1k" unit="/s" data={charts.blkW} color={Y} type="bar" />
              <MetricBlock label="Deadlocks" value="0" data={charts.dead} color={R} type="bar" />
              <MetricBlock label="Replication lag" value="24" unit="ms" data={charts.lag} color={B} type="line" />
            </div>
          </SectionCard>
        </div>
      )}

      {tab === "query" && (
        <SectionCard title="Query Execution" scope="Latence · throughput · slow queries" icon={Zap} tone="warning">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricBlock label="Avg query time" value="12.4" unit="ms" data={charts.qAvg} color={G} type="line" />
            <MetricBlock label="p95 latency" value="84" unit="ms" data={charts.qP95} color={Y} type="line" />
            <MetricBlock label="Queries/s" value="2.4k" data={charts.qps} color={B} type="area" />
            <MetricBlock label="Slow queries" value="7" unit=">1s" data={charts.slow} color={R} type="bar" />
          </div>
          <div className="mt-3 space-y-1.5">
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-mono text-muted-foreground">SELECT * FROM consultations WHERE …</span>
              <span className="text-accent">1.8s</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-mono text-muted-foreground">SELECT … FROM lab_results JOIN …</span>
              <span className="text-[oklch(0.85_0.15_85)]">820ms</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="font-mono text-muted-foreground">UPDATE appointments SET status = …</span>
              <span className="text-[oklch(0.85_0.15_85)]">640ms</span>
            </div>
          </div>
        </SectionCard>
      )}

      {tab === "logs" && (
        <SectionCard title="Error Logs" scope={`${filteredLogs.length}/${ALL_LOGS.length} entries · ${RANGE_META[range].label}`} icon={AlertTriangle} tone="danger">
          <div className="grid grid-cols-3 gap-2">
            <MetricBlock label="ERROR" value="14" data={charts.err} color={R} type="bar" />
            <MetricBlock label="WARN" value="38" data={charts.warn} color={Y} type="bar" />
            <MetricBlock label="FATAL" value="2" data={charts.fatal} color={R} type="bar" />
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher (message ou source)…"
                className="w-full rounded-full bg-white/5 py-1.5 pl-8 pr-3 text-[11px] outline-none ring-1 ring-white/10 placeholder:text-muted-foreground focus:ring-white/20"
              />
            </div>
            <div className="inline-flex items-center gap-1 rounded-full bg-white/5 p-1">
              {(["ERROR", "WARN", "INFO", "FATAL"] as Level[]).map((lvl) => {
                const active = levels.has(lvl);
                const color = LEVEL_TONE[lvl];
                return (
                  <button
                    key={lvl}
                    onClick={() => toggleLevel(lvl)}
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium transition"
                    style={
                      active
                        ? { background: withAlpha(color, 0.18), color }
                        : { color: "oklch(0.7 0.02 240)" }
                    }
                  >
                    {lvl}
                  </button>
                );
              })}
            </div>
          </div>

          <ul className="mt-3 space-y-1.5">
            {filteredLogs.length === 0 ? (
              <li className="rounded-lg bg-white/[0.02] px-3 py-4 text-center text-[11px] text-muted-foreground">
                Aucune entrée correspondante.
              </li>
            ) : (
              filteredLogs.map((l, i) => <LogRow key={i} entry={l} />)
            )}
          </ul>
        </SectionCard>
      )}

      {tab === "relations" && (
        <SectionCard title="Relation Metrics" scope="Tables · index · bloat · vacuum" icon={Layers} tone="success">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <div>
              <div className="mb-2 grid grid-cols-[1fr_72px_72px_120px] gap-2 px-3 text-[10px] uppercase tracking-wider text-muted-foreground">
                <span>Table</span>
                <span className="text-right">Size</span>
                <span className="text-right">Rows</span>
                <span>Bloat</span>
              </div>
              <ul className="space-y-1.5">
                <RelationRow name="public.consultations" size="42 GB" rows="8.4M" ratio={18} tone="success" />
                <RelationRow name="public.lab_results" size="28 GB" rows="12.1M" ratio={34} tone="warning" />
                <RelationRow name="public.audit_log" size="64 GB" rows="48.2M" ratio={62} tone="danger" />
                <RelationRow name="public.appointments" size="9.2 GB" rows="2.1M" ratio={12} tone="success" />
                <RelationRow name="public.patients" size="4.8 GB" rows="412k" ratio={8} tone="success" />
                <RelationRow name="storage.objects" size="18 GB" rows="1.8M" ratio={22} tone="warning" />
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MetricBlock label="Seq scans/s" value="48" data={charts.seq} color={B} type="line" />
              <MetricBlock label="Index scans/s" value="2.1k" data={charts.idx} color={G} type="line" />
              <MetricBlock label="Index hit ratio" value="99.2" unit="%" data={charts.hit} color={G} type="line" />
              <MetricBlock label="Vacuum jobs" value="6" unit="/h" data={charts.vac} color={P} type="bar" />
            </div>
          </div>
        </SectionCard>
      )}

      {tab === "security" && (
        <SectionCard title="Security" scope="Auth failures · roles · RLS · SSL" icon={ShieldCheck} tone="warning">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricBlock label="Auth failures" value="3" unit="/h" data={charts.err} color={R} type="bar" />
            <MetricBlock label="Active sessions" value="42" data={charts.conn} color={G} type="area" />
            <MetricBlock label="SSL connections" value="100" unit="%" data={charts.cache} color={G} type="line" />
            <MetricBlock label="Privilege grants" value="2" unit="/24h" data={charts.warn} color={Y} type="bar" />
          </div>
          <ul className="mt-3 space-y-1.5">
            <li className="grid grid-cols-[80px_60px_140px_1fr] items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2 text-[11px]">
              <span className="font-mono text-muted-foreground">14:31:02</span>
              <span className="rounded px-1.5 py-0.5 text-center text-[10px] font-medium" style={{ background: withAlpha(R, 0.18), color: R }}>DENY</span>
              <span className="truncate font-mono text-muted-foreground">pg_hba.conf</span>
              <span className="truncate">connection refused for user "postgres" from 51.142.x.x</span>
            </li>
            <li className="grid grid-cols-[80px_60px_140px_1fr] items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2 text-[11px]">
              <span className="font-mono text-muted-foreground">13:58:14</span>
              <span className="rounded px-1.5 py-0.5 text-center text-[10px] font-medium" style={{ background: withAlpha(Y, 0.18), color: Y }}>GRANT</span>
              <span className="truncate font-mono text-muted-foreground">role:analyst_ro</span>
              <span className="truncate">SELECT granted on public.consultations</span>
            </li>
            <li className="grid grid-cols-[80px_60px_140px_1fr] items-center gap-2 rounded-lg bg-white/[0.02] px-3 py-2 text-[11px]">
              <span className="font-mono text-muted-foreground">12:42:08</span>
              <span className="rounded px-1.5 py-0.5 text-center text-[10px] font-medium" style={{ background: withAlpha(G, 0.18), color: G }}>RLS</span>
              <span className="truncate font-mono text-muted-foreground">public.patients</span>
              <span className="truncate">policy "patients_self_read" enabled</span>
            </li>
          </ul>
        </SectionCard>
      )}

      {fullscreen && (
        <div className="fixed inset-0 z-50 flex flex-col gap-4 overflow-auto bg-background/95 p-6 backdrop-blur">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">PostgreSQL · Live</div>
              <h2 className="text-lg font-medium">Database Metrics — Fullscreen</h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">
                Sync : {lastSync.toLocaleTimeString()} · {RANGE_META[range].label}
              </span>
              <button
                onClick={() => setFullscreen(false)}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1.5 text-[11px] ring-1 ring-white/10 hover:bg-white/10"
              >
                <X className="h-3.5 w-3.5" /> Fermer
              </button>
            </div>
          </div>

          <Toolbar range={range} setRange={setRange} auto={auto} setAuto={setAuto} onRefresh={refresh} lastSync={lastSync} />

          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <HeaderKpi icon={Database} value="42" label="Active connections" delta="+3" tone="success" seed={1} />
            <HeaderKpi icon={Gauge} value="68%" label="Cache hit ratio" delta="+2%" tone="success" seed={2} />
            <HeaderKpi icon={HardDrive} value="187 GB" label="Database size" delta="+1.2GB" tone="warning" seed={3} />
            <HeaderKpi icon={AlertTriangle} value="14" label="Errors (24h)" delta="+4" tone="danger" seed={4} />
          </div>

          <SectionCard title="Live Metrics" scope={`Streaming · ${RANGE_META[range].label}`} icon={Database} tone="success">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <MetricBlock label="Connections" value="42" unit="/ 100" data={charts.conn} color={G} />
              <MetricBlock label="Transactions/s" value="1.2k" data={charts.tx} color={B} type="line" />
              <MetricBlock label="Tuples fetched" value="48k" unit="/s" data={charts.tuples} color={P} type="area" />
              <MetricBlock label="Buffer cache hit" value="98.4" unit="%" data={charts.cache} color={G} type="line" />
              <MetricBlock label="Blocks read" value="3.2k" unit="/s" data={charts.blkR} color={Y} type="bar" />
              <MetricBlock label="Blocks written" value="1.1k" unit="/s" data={charts.blkW} color={Y} type="bar" />
              <MetricBlock label="Deadlocks" value="0" data={charts.dead} color={R} type="bar" />
              <MetricBlock label="Replication lag" value="24" unit="ms" data={charts.lag} color={B} type="line" />
            </div>
          </SectionCard>

          <SectionCard title="History" scope="Tendances 24h" icon={Activity} tone="neutral">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <MetricBlock label="Connections (24h)" value="avg 38" data={series(21, 96, 38, 14, tick)} color={G} type="area" />
              <MetricBlock label="Transactions/s (24h)" value="avg 1.1k" data={series(22, 96, 1100, 400, tick)} color={B} type="line" />
              <MetricBlock label="Cache hit (24h)" value="avg 97.8%" data={series(23, 96, 97, 2, tick)} color={G} type="line" />
              <MetricBlock label="Errors (24h)" value="14 total" data={series(24, 96, 0.6, 0.8, tick)} color={R} type="bar" />
            </div>
          </SectionCard>
        </div>
      )}

      <div className="hidden">
        <Activity />
      </div>
    </div>
  );
}
